<template>
    <div>
        <h6><b>Non-Profile Medications</b></h6>
        <div v-show="!disabled">
            <b-row>
                <b-col md="6" cols="12">
                    <search-component v-model="drug" label="Drug" search-u-r-l="/Drug" :params="{ onlyActive: false }"
                        :disabled="disabled" clearable />
                    <AllergyWarningComponent :patient-id="patient && +patient.id" :drug="drug" />
                    <b-row>
                        <b-col>
                            <fg-input label="Start Date *">
                                <el-date-picker v-model="startDate" format="MM/dd/yyyy" type="date" :disabled="disabled" />
                            </fg-input>
                        </b-col>
                        <b-col>
                            <fg-input label="End Date">
                                <el-date-picker v-model="endDate" format="MM/dd/yyyy" type="date" :disabled="disabled"
                                    :picker-options="datePickerOptions(startDate)" />
                            </fg-input>
                        </b-col>
                        <b-col>
                            <fg-input label="Origin">
                                <b-form-select v-model="origin" :options="originOptions" :disabled="disabled" />
                            </fg-input>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col md="6" cols="12" class="d-flex align-items-start">
                    <fg-input label="Notes" class="flex-fill">
                        <textarea v-model="notes" class="form-control" rows="4" :disabled="disabled"
                            placeholder="Add notes about this medication"></textarea>
                    </fg-input>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button variant="success" @click="addDrug" :disabled="disabled">Add Medication</b-button>
                </b-col>
            </b-row>
        </div>
        <b-row>
            <b-col>
                <b-table ref="other-medication-table" :items="otherMedications" :fields="tableFields"
                    select-mode="single" selectable selected-variant="" no-select-on-click>
                    <template #cell(drugName)="row">
                        <div v-if="row.rowSelected">
                            <div class="mb-2">
                                <strong>{{ row.item.drugName || 'No drug specified' }}</strong>
                            </div>
                            <fg-input label="Notes" class="mb-0">
                                <textarea v-model="row.item.notes" class="form-control textarea-autosize" rows="2" :disabled="disabled"></textarea>
                            </fg-input>
                        </div>
                        <div v-else>
                            <div v-if="row.item.drugName">
                                {{ row.item.drugName }}
                                <div v-if="row.item.notes" class="text-muted small"><strong>{{ row.item.notes }}</strong></div>
                            </div>
                            <div v-else-if="row.item.notes">{{ row.item.notes }}</div>
                            <span v-else class="text-muted font-italic">No information provided</span>
                        </div>
                    </template>
                    <template #cell(endDate)="row">
                        <fg-input v-if="row.rowSelected" class="mb-0">
                            <el-date-picker v-model="row.item.endDate" format="MM/dd/yyyy" type="date"
                                :picker-options="datePickerOptions(row.item.startDate)" :disabled="disabled" />
                        </fg-input>
                        <div v-else>
                            {{ row.value }}
                        </div>
                    </template>
                    <template #cell(edit)="row">
                        <div class="text-right table-icon-actions">
                            <template v-if="row.rowSelected">
                                <a v-b-tooltip.hover title="Save" href="#" @click.prevent="edit(row)" key="save-btn">
                                    <b-icon icon="save" variant="primary" />
                                </a>
                                <a v-b-tooltip.hover title="Cancel" href="#" v-if="row.rowSelected"
                                    @click.prevent="cancelEdit(row)">
                                    <b-icon icon="x-lg" variant="" />
                                </a>
                            </template>
                            <template v-else>
                                <a v-b-tooltip.hover title="Edit" href="#" @click.prevent="edit(row)" key="edit-btn">
                                    <b-icon icon="pencil-square" />
                                </a>
                                <a v-if="canManageNonProfileMeds" v-b-tooltip.hover title="Delete" href="#"
                                    @click.prevent="confirmDelete(row.item)" key="delete-btn">
                                    <b-icon icon="trash" variant="danger" />
                                </a>
                            </template>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <b-row v-if="patient && patient.id && requireRemovalReason">
            <b-col>
                <b-button variant="link" size="sm" class="px-0" @click="toggleArchived">
                    {{ showArchived ? 'Hide' : 'Show' }} removed medications ({{ archivedMedications.length }})
                </b-button>
                <b-table v-if="showArchived && archivedMedications.length > 0" :items="archivedMedications"
                    :fields="archivedTableFields" small />
                <div v-else-if="showArchived" class="text-muted small">No removed medications.</div>
            </b-col>
        </b-row>

        <RemovalReasonModal ref="reasonModal" />
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import ClinicalPermissionsMixin from '@/mixins/ClinicalPermissionsMixin';
import SearchComponent from "@/components/SearchComponent.vue";
import AllergyWarningComponent from "@/components/Drug/AllergyWarningComponent.vue";
import RemovalReasonModal from '@/components/Patient/RemovalReasonModal.vue';
import { Patient } from "@/models/Patient";
import axios from "axios";
import { OtherMedication, OtherMedicationViewModel } from "@/models/Drug/OtherMedication";
import { MacOrigin, macOriginLabel } from "@/models/MacOrigin";
import { enumToOptions } from "@/util/EnumOptions";
import { DatePicker } from "element-ui";
import { Drug } from "@/models/Drug/Drug";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { BTable, BvTableFieldArray } from "bootstrap-vue";
import moment from "moment";
// Element UI DatePickerOptions type - defined inline
interface DatePickerOptions {
  disabledDate?: (date: Date) => boolean;
}
import { toRaw } from 'vue';

@Component({
    components: {
        SearchComponent,
        AllergyWarningComponent,
        RemovalReasonModal,
        [DatePicker.name]: DatePicker,
    }
})
export default class OtherMedicationsComponent extends Mixins(ClinicalPermissionsMixin) {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ default: false, type: Boolean }) disabled!: boolean;
    @Prop({ default: false, type: Boolean }) onlyActive!: boolean;
    @Prop({ default: null }) defaultOrigin!: MacOrigin | null;

    @Ref("other-medication-table") medTable!: BTable;
    @Ref("reasonModal") reasonModal!: RemovalReasonModal;

    protected otherMedications: OtherMedicationViewModel[] = [];
    protected archivedMedications: OtherMedicationViewModel[] = [];
    protected startDate = moment().startOf('day').toDate();
    protected endDate: Date | null = null;
    protected drug: Drug = new Drug();
    protected notes: string = '';
    protected origin: MacOrigin | null = null;
    protected readonly originOptions = enumToOptions(MacOrigin, { includeNone: true });
    protected requireRemovalReason = false;
    protected showArchived = false;
    // Archived list is lazy-loaded on first expand and cached; invalidated when a removal changes it.
    private archivedLoaded = false;

    mounted() {
        this.origin = this.defaultOrigin;
        this.fetchOtherMedications();
        this.fetchRemovalReasonConfig();
    }

    async fetchRemovalReasonConfig() {
        try {
            const resp = await axios.get<boolean>('/Configuration/RequireReasonForPatientMacRemoval');
            this.requireRemovalReason = resp.data === true;
        } catch (err) {
            // Default to prompting on a transient failure: the server enforces the rule independently,
            // so skipping the prompt would only produce a confusing 400 the user can't act on.
            console.error("Error loading removal-reason config", err);
            this.requireRemovalReason = true;
        }
    }

    protected selectedOtherMedication: OtherMedicationViewModel | null = null;

    async edit(row: any) {
        try {
            if (row.rowSelected) {
                row.unselectRow();
                if (this.patient?.id) {
                    await axios.post("/api/OtherMedication", row.item);
                    this.fetchOtherMedications();
                }
            } else {
                this.selectedOtherMedication = structuredClone(toRaw(row.item)) as OtherMedicationViewModel;
                this.medTable.clearSelected();
                row.selectRow();
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async cancelEdit(row: any) {
        row.item.endDate = this.selectedOtherMedication?.endDate;
        row.item.notes = this.selectedOtherMedication?.notes;
        row.unselectRow();
    }

    async addDrug() {
        // Validate: either drug or notes must be provided
        if (!this.drug.drugId && !this.notes?.trim()) {
            this.$notification(NotificationOptions.error("Please provide either a drug or notes"));
            return;
        }

        // Validate: start date is required
        if (!this.startDate) {
            this.$notification(NotificationOptions.error("Start Date is required"));
            return;
        }

        if (!this.patient?.id) {
            this.otherMedications.push({
                drugId: this.drug.drugId || undefined,
                drugName: this.drug.productNameShort || undefined,
                source: this.drug.drugId ? this.drug.source : undefined,
                startDate: this.startDate,
                endDate: this.endDate,
                notes: this.notes || undefined,
                origin: this.origin
            } as OtherMedicationViewModel);
            this.drug = new Drug();
            this.notes = '';
            this.origin = this.defaultOrigin;
            return;
        }

        const otherMed = {
            drugId: this.drug.drugId || undefined,
            source: this.drug.drugId ? this.drug.source : undefined,
            patientId: this.patient.id,
            startDate: this.startDate,
            endDate: this.endDate,
            notes: this.notes || undefined,
            origin: this.origin
        } as OtherMedication;

        this.addDrugToPatient(otherMed);
    }

    async addDrugToPatient(otherMed: OtherMedication) {
        try {
            await axios.post(`/api/OtherMedication`, otherMed);
            this.drug = new Drug();
            this.notes = '';
            this.origin = this.defaultOrigin;
            this.fetchOtherMedications();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async commitAllForPatient(patientId: number) {
        await Promise.all(this.otherMedications.map(om =>
            axios.post(`/api/OtherMedication`, {
                ...om,
                patientId: patientId
            })
        ));
    }

    async confirmDelete(item: OtherMedicationViewModel) {
        const drugDisplay = item.drugName || item.notes || 'this medication';

        let reason: string | undefined;
        if (this.requireRemovalReason) {
            const entered = await this.reasonModal.prompt({
                itemLabel: drugDisplay,
                required: true,
                title: 'Remove Non-Profile Medication',
            });
            if (entered === null) return;
            reason = entered;
        } else {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to remove "${drugDisplay}"?`,
                { title: 'Remove Non-Profile Medication', okVariant: 'danger', okTitle: 'Remove' }
            );
            if (!confirmed) return;
        }

        try {
            await axios.delete(`/api/OtherMedication/${item.id}`, { params: { patientId: this.patient.id, reason } });
            this.$notification(NotificationOptions.success('Non-profile medication removed.'));
            this.fetchOtherMedications();
            this.archivedLoaded = false;
            if (this.showArchived) await this.fetchArchivedMedications();
        } catch (err: any) {
            if (err?.response?.status === 404) {
                this.$notification(NotificationOptions.error('Medication not found. It may have already been removed.'));
                this.fetchOtherMedications();
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    async toggleArchived() {
        this.showArchived = !this.showArchived;
        if (this.showArchived && !this.archivedLoaded) await this.fetchArchivedMedications();
    }

    async fetchArchivedMedications() {
        if (!this.patient?.id) {
            this.archivedMedications = [];
            return;
        }
        try {
            const resp = await axios.get<OtherMedicationViewModel[]>("/api/OtherMedication/Archived",
                { params: { patientId: this.patient.id } });
            this.archivedMedications = resp.data;
            this.archivedLoaded = true;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            this.archivedMedications = [];
        }
    }

    get archivedTableFields(): BvTableFieldArray {
        return [
            { key: "drugName", label: "Drug", formatter: (_v: any, _k: any, item: any) => item.drugName || item.notes || "" },
            { key: "origin", label: "Origin", formatter: (value: any) => macOriginLabel(value) },
            { key: "removed", label: "Removed Date", formatter: (value: any) => value ? moment(value).format("L") : "" },
            { key: "removedBy", label: "Removed By" },
            { key: "removalReason", label: "Reason" },
        ];
    }

    datePickerOptions(startDate: any): DatePickerOptions {
        return {
            disabledDate(date) {
                // End Date must fall after the Start Date's calendar day. Compare at day
                // granularity so the minimum stays day-after-start whether the start value carries
                // a time-of-day (legacy rows) or is normalized to midnight.
                return moment(date).isSameOrBefore(startDate, 'day');
            }
        };
    }

    get tableFields(): BvTableFieldArray {
        return [
            { key: "drugName", label: "Drug" },
            { key: "startDate", label: "Start Date", formatter: (value: any) => moment(value).format("L") },
            { key: "endDate", label: "End Date", formatter: (value: any) => value ? moment(value).format("L") : "", class: "end-date-col" },
            { key: "origin", label: "Origin", formatter: (value: any) => macOriginLabel(value) },
            { key: "edit", label: "", class: this.disabled ? "d-none" : "edit-col", },
        ];
    }

    async fetchOtherMedications() {
        if (!this.patient?.id) {
            this.otherMedications = [];
            return;
        }
        try {
            const resp = await axios.get<OtherMedicationViewModel[]>("/api/OtherMedication", { params: { patientId: this.patient.id } });
            if (!this.onlyActive) {
                this.otherMedications = resp.data;
                return;
            }
            this.otherMedications = resp.data.filter(d => moment(d.startDate).isSameOrBefore(moment(), 'day') && (!d.endDate || moment(d.endDate).isAfter(moment(), 'day')));
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            this.otherMedications = [];
        }
    }

    @Watch("patient")
    patientChanged() {
        this.drug = new Drug();
        this.notes = '';
        this.origin = this.defaultOrigin;
        this.fetchOtherMedications();
        // Invalidate the archived cache so a later expand re-fetches for the new patient.
        this.archivedLoaded = false;
        this.archivedMedications = [];
        this.showArchived = false;
    }

    @Watch("drug")
    drugChanged(newValue: Drug | null) {
        // Handle when the search component is cleared (X button clicked)
        if (!newValue || !newValue.drugId) {
            // Only reassign when the value is truly null: restoring an empty Drug keeps the declared
            // non-nullable type honest so addDrug's this.drug.drugId dereference doesn't throw on a
            // notes-only add. Guarding on !newValue is required because this handler watches `drug` —
            // unconditionally assigning a fresh reference here would re-enter the watcher forever
            // (a new Drug() has no drugId, so it would keep matching this branch). An empty Drug is
            // therefore the fixed point, and assigning null converges to it in one extra pass.
            if (!newValue) {
                this.drug = new Drug();
            }
            this.startDate = moment().startOf('day').toDate();
            this.endDate = null;
        }
    }
}
</script>

<!--suppress CssUnusedSymbol -->
<style scoped>
::v-deep(.end-date-col) {
    width: 200px;
}

::v-deep(.edit-col) {
    width: 40px;
}

::v-deep(th) {
    font-weight: normal !important;
}
</style>
